import React, { useEffect, useState, useRef } from 'react'
import { Flex, FlexCenter } from '../Flex'
import { InputModal } from '../InputModal'
import { Close } from '../Icon'
import { H4 } from '../Heading'
import { contactUsDetailsSchema } from '../../utils/schema'
import { GuestContactUsFormProps_v1, ContactUsModalProps } from '../../types'
import { ContactUsForm } from './contact-us-form'
import { userServices, contactsServices } from '../../../services'
import { StatusLabel } from '../StatusLabel'
import './contact-us.scss'
import { useDispatch, useSelector } from 'react-redux'
import { showBanner } from '../../../global-state/banner-slice'
import { RootState } from '../../../store'
import ReCAPTCHA from 'react-google-recaptcha'
import { trackGaLeadFormSubmit } from '../../analytics/google-analytics'

const ContactUsModal = ({
  active,
  handleOnClose,
  initialSubject,
  initialEmail,
}: ContactUsModalProps) => {
  const reRef = useRef<ReCAPTCHA>(null)
  const dispatch = useDispatch()
  const [apiResponse, setApiResponse] = useState<{
    type: 'success' | 'error'
    message: string
  }>({
    type: 'success',
    message: '',
  })

  const { authToken, email } = useSelector((state: RootState) => state.investor)
  const [initialValues, setInitialValues] = useState<GuestContactUsFormProps_v1>({
    firstName: '',
    lastName: '',
    email: email,
    subject: 'KYC Verification',
    message: '',
  })
  const [captchaToken, setCaptchaToken] = React.useState<string | null>(null)

  useEffect(() => {
    if (active && (initialSubject !== undefined || initialEmail !== undefined)) {
      setInitialValues((prev) => ({
        ...prev,
        ...(initialSubject !== undefined && {
          subject: initialSubject as GuestContactUsFormProps_v1['subject'],
        }),
        ...(initialEmail !== undefined && { email: initialEmail }),
      }))
    }
  }, [active, initialSubject, initialEmail])

  useEffect(() => {
    const getLoggedInUser = async () => {
      const response = await userServices.getProfileForCurrentUser_v1({
        Authorization: authToken ? authToken : '',
      })
      if (response.status === 200) {
        setInitialValues((prev) => ({
          ...prev,
          firstName: response.data.name.split(' ')[0],
          lastName: response.data.name.split(' ')[1],
          email: initialEmail ?? response.data.email ?? email,
        }))
      }
    }
    if (authToken) getLoggedInUser()
  }, [])

  const handleSubmit = async (values: GuestContactUsFormProps_v1) => {
    reRef.current?.reset()

    const data = await contactsServices.submitGuestContactForm_v1(values, {
      'g-recaptcha-response': captchaToken ? captchaToken : '',
    })

    if (!!data && data.status === 200) {
      setApiResponse({
        type: 'success',
        message: data.data.message,
      })
      trackGaLeadFormSubmit('guest_contact')

      dispatch(
        showBanner({
          text: 'Query submitted successfully',
          variant: 'success-banner',
        }),
      )
      handleOnClose()
    } else {
      const message = data.data ? data.data.message : 'Error recording your data'
      setApiResponse({
        type: 'error',
        message: message,
      })

      dispatch(
        showBanner({
          text: 'Something went wrong! Please try again.',
          variant: 'error-banner',
        }),
      )
    }
  }

  return (
    <div>
      {active ? (
        <InputModal>
          <FlexCenter variant='column' classes={['contact-us-modal-container']}>
            <div
              className='contact-us-card-container p-24 contact-us-form'
              data-testid='contact-us-modal'
            >
              <Flex variant='space-between' classes={['mb-32']}>
                <H4 text='Reach Us' />
                <Close type='default-light' onClick={handleOnClose} />
              </Flex>

              <ContactUsForm
                validationSchema={contactUsDetailsSchema}
                handleSubmit={handleSubmit}
                initialValues={initialValues}
                reRef={reRef}
                captchaToken={captchaToken}
                setCaptchaToken={setCaptchaToken}
              />
              {apiResponse.message.length > 0 && (
                <FlexCenter>
                  <StatusLabel
                    type={apiResponse.type}
                    text={apiResponse.message}
                    variant={'bold'}
                    dataTestId='contact-us-modal-api-response'
                  />
                </FlexCenter>
              )}
            </div>
          </FlexCenter>
        </InputModal>
      ) : null}
    </div>
  )
}
export { ContactUsModal }
